import { useState } from 'react';
import { formaterMontant } from '../lib/format';
import { totalReductions, type SaisieLigneReduction } from '../lib/reductions';
import type { TypeReduction } from '../types';

/**
 * Le bloc REDUCTIONS d'une inscription (2026-10-12) : une case par type
 * de reduction de l'annee, avec son montant ; la remise libre ouvre un
 * montant et un motif obligatoires. Les reductions se cumulent.
 *
 * Un type DESACTIVE n'est propose que s'il est deja coche : la ligne
 * posee avant sa desactivation reste lisible et se reenregistre avec le
 * reste de la fiche.
 *
 * La part NON DETAILLEE est celle des inscriptions saisies avant le
 * referentiel (ou posee par un transfert) : elle est affichee telle
 * quelle et ne se retire qu'explicitement — enregistrer la fiche pour
 * corriger un creneau ne doit pas la faire disparaitre.
 */
export function ChampReductions({
  types,
  typeActiviteId,
  lignes,
  onChange,
  nonDetaillee,
  onRetirerNonDetaillee,
  disabled = false,
}: {
  types: TypeReduction[];
  /** Le type d'activite de l'inscription : seuls les types de reduction qui s'y appliquent sont proposes. */
  typeActiviteId: string | null;
  lignes: SaisieLigneReduction[];
  onChange: (lignes: SaisieLigneReduction[]) => void;
  /** En centimes. */
  nonDetaillee: number;
  onRetirerNonDetaillee?: () => void;
  disabled?: boolean;
}) {
  const cochee = (id: string) => lignes.find((l) => l.typeReductionId === id);
  // Un type restreint a certains types d'activite ne se propose que sur
  // eux ; deja coche, il reste visible — comme un type desactive.
  const applicable = (t: TypeReduction) =>
    t.toutes_activites ||
    (typeActiviteId !== null && t.types_activite.some((x) => x.id === typeActiviteId));
  const proposes = types.filter((t) => cochee(t.id) || (t.actif && applicable(t)));

  const basculer = (type: TypeReduction) => {
    if (cochee(type.id)) {
      onChange(lignes.filter((l) => l.typeReductionId !== type.id));
    } else {
      onChange([...lignes, { typeReductionId: type.id, montant: '', commentaire: '' }]);
    }
  };

  const modifier = (id: string, champ: 'montant' | 'commentaire', valeur: string) => {
    onChange(lignes.map((l) => (l.typeReductionId === id ? { ...l, [champ]: valeur } : l)));
  };

  const total = totalReductions(lignes, types, nonDetaillee);

  // LE BLOC EST REPLIE (2026-10-13) : la modale du detail est longue, et
  // les types ne servent qu'au moment de poser une reduction. Replie, la
  // ligne de titre resume ce qui est applique ; un clic deplie les cases.
  const [ouvert, setOuvert] = useState(false);
  const appliquees = lignes
    .map((l) => types.find((t) => t.id === l.typeReductionId))
    .filter((t): t is TypeReduction => Boolean(t))
    .map((t) => t.libelle);
  if (nonDetaillee > 0) appliquees.push('non détaillée');
  const resume =
    appliquees.length > 0 ? `${appliquees.join(' · ')} — ${formaterMontant(total)}` : 'aucune';

  return (
    <div className="rounded-md border border-encre-200 p-3">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setOuvert(!ouvert)}
        aria-expanded={ouvert}
      >
        <span className="etiquette !mb-0">Réductions</span>
        <span className="flex min-w-0 items-center gap-2 text-sm text-encre-600">
          <span className="truncate">{resume}</span>
          <span aria-hidden className="text-xs text-encre-400">
            {ouvert ? '▾' : '▸'}
          </span>
        </span>
      </button>
      {ouvert && proposes.length === 0 && nonDetaillee === 0 && (
        <p className="text-xs text-encre-400">
          Aucun type de réduction défini pour cette année (Référentiels → Types de réduction).
        </p>
      )}
      {ouvert && (
        <ul className="mt-2 space-y-2">
          {proposes.map((type) => {
            const ligne = cochee(type.id);
            return (
              <li key={type.id}>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={Boolean(ligne)}
                    disabled={disabled}
                    onChange={() => basculer(type)}
                  />
                  <span className="flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span>
                        {type.libelle}
                        {!type.actif && (
                          <span className="ml-1 text-xs text-encre-400">(désactivé)</span>
                        )}
                      </span>
                      {/* LE MONTANT FIGE NE SE TOUCHE PAS : il s'affiche. */}
                      {type.montant !== null && (
                        <span className="tabulaire text-encre-600">
                          {formaterMontant(type.montant)}
                        </span>
                      )}
                    </span>
                    {type.aide && <span className="block text-xs text-encre-400">{type.aide}</span>}
                  </span>
                </label>
                {ligne && type.montant === null && (
                  <div className="ml-6 mt-1 grid grid-cols-3 gap-2">
                    <input
                      className="champ tabulaire"
                      value={ligne.montant}
                      onChange={(e) => modifier(type.id, 'montant', e.target.value)}
                      inputMode="decimal"
                      placeholder="Montant (€)"
                      required
                      disabled={disabled}
                    />
                    <input
                      className="champ col-span-2"
                      value={ligne.commentaire}
                      onChange={(e) => modifier(type.id, 'commentaire', e.target.value)}
                      placeholder="Motif (obligatoire)"
                      required
                      disabled={disabled}
                    />
                  </div>
                )}
              </li>
            );
          })}
          {nonDetaillee > 0 && (
            <li className="flex items-baseline justify-between gap-2 rounded-md bg-amber-50 px-2 py-1 text-sm text-amber-800">
              <span>
                Réduction non détaillée
                <span className="block text-xs text-amber-700">
                  Saisie avant les types de réduction : cochez le bon type, puis retirez-la.
                </span>
              </span>
              <span className="flex items-baseline gap-2">
                <span className="tabulaire">{formaterMontant(nonDetaillee)}</span>
                {onRetirerNonDetaillee && !disabled && (
                  <button
                    type="button"
                    className="text-xs underline hover:text-amber-950"
                    onClick={onRetirerNonDetaillee}
                  >
                    Retirer
                  </button>
                )}
              </span>
            </li>
          )}
        </ul>
      )}
      {ouvert && (lignes.length > 0 || nonDetaillee > 0) && (
        <p className="mt-2 flex justify-between border-t border-encre-100 pt-2 text-sm">
          <span className="text-encre-500">Total des réductions</span>
          <span className="tabulaire font-medium">{formaterMontant(total)}</span>
        </p>
      )}
    </div>
  );
}
